import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getKaizenEvents, getKaizenEvent, createKaizenEvent, updateKaizenEvent, deleteKaizenEvent, archiveResource } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { emptyStateMsg } from '@/utils/styleHelpers';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { PageTabs } from '@/components/PageTabs';
import {
  Plus,
  ArrowLeft,
  Search,
  Filter,
  Trash2,
  Zap,
  Calendar,
  Users,
  Target,
  CheckCircle2,
  Clock,
  Play,
  Pause,
  FolderKanban,
  Archive
} from 'lucide-react';

const HUB_TABS = [
  { path: '/projects', label: 'DMAIC Projects', icon: FolderKanban },
  { path: '/kaizen', label: 'Kaizen Events', icon: Zap },
];

const industries = [
  { value: 'logistics', label: 'Logistics' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
];

const statusColors = {
  planned: 'bg-blue-50 text-blue-600',
  in_progress: 'bg-amber-50 text-amber-600',
  completed: 'bg-emerald-50 text-emerald-600',
  cancelled: 'bg-rose-50 text-rose-600'
};

// Kaizen Events List
export const KaizenListPage = () => {
  const { isGuest } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');

  useEffect(() => {
    loadEvents();
  }, [industryFilter]);

  const loadEvents = async () => {
    try {
      const data = await getKaizenEvents(industryFilter !== 'all' ? industryFilter : null);
      setEvents(data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(e => 
    e.title.toLowerCase().includes(search.toLowerCase()) ||
    e.area.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="kaizen-list-page">
        <PageTabs tabs={HUB_TABS} testIdPrefix="kaizen-tab" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Kaizen Events</h1>
            <p className="text-muted-foreground font-body mt-1">
              Rapid improvement workshops for focused problem-solving
            </p>
          </div>
          <Button asChild data-testid="new-kaizen-btn" className={isGuest ? 'hidden' : ''}>
            <Link to="/kaizen/new">
              <Plus className="h-4 w-4 mr-2" /> New Event
            </Link>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-kaizen"
            />
          </div>
          <Select value={industryFilter} onValueChange={setIndustryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-industry">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Industries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              {industries.map((ind) => (
                <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredEvents.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event) => (
              <div 
                key={event.event_id} 
                className={`block ${isGuest ? 'cursor-default' : 'cursor-pointer'}`}
                data-testid={`kaizen-card-${event.event_id}`}
                onClick={() => { if (!isGuest) { window.location.href = `/kaizen/${event.event_id}`; } }}
              >
                <Card className="h-full card-hover">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <Badge className="capitalize">{event.industry}</Badge>
                      <Badge className={statusColors[event.status]}>
                        {event.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <CardTitle className="mt-3 line-clamp-1">{event.title}</CardTitle>
                    <CardDescription className="line-clamp-2">{event.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {new Date(event.start_date).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {event.participants?.length || 0}
                      </span>
                    </div>
                    <p className="text-sm"><strong>Area:</strong> {event.area}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <Card className="py-16">
            <CardContent className="text-center">
              <Zap className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No events found</h3>
              <p className="text-muted-foreground font-body mb-4">
                {emptyStateMsg(isGuest, search, 'Plan your first Kaizen event')}
              </p>
              {!isGuest && (
                <Button asChild>
                  <Link to="/kaizen/new">
                    <Plus className="h-4 w-4 mr-2" /> Plan Event
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

// Create Kaizen Event
export const CreateKaizenPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    area: '',
    industry: 'logistics',
    objective: '',
    expected_outcomes: [],
    participants: [],
    start_date: '',
    end_date: ''
  });
  const [outcomeInput, setOutcomeInput] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addOutcome = () => {
    if (outcomeInput.trim()) {
      setFormData(prev => ({
        ...prev,
        expected_outcomes: [...prev.expected_outcomes, outcomeInput.trim()]
      }));
      setOutcomeInput('');
    }
  };

  const removeOutcome = (index) => {
    setFormData(prev => ({
      ...prev,
      expected_outcomes: prev.expected_outcomes.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const event = await createKaizenEvent(formData);
      toast.success('Event created successfully!');
      navigate(`/kaizen/${event.event_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="create-kaizen-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/kaizen')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Plan Kaizen Event</h1>
            <p className="text-muted-foreground font-body">Organize a rapid improvement workshop</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Event Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Picking Process Optimization Blitz"
                  required
                  data-testid="kaizen-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="What will this event focus on?"
                  rows={3}
                  required
                  data-testid="kaizen-description"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="area">Target Area *</Label>
                  <Input
                    id="area"
                    value={formData.area}
                    onChange={(e) => handleChange('area', e.target.value)}
                    placeholder="e.g., Warehouse Zone A"
                    required
                    data-testid="kaizen-area"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                    <SelectTrigger data-testid="kaizen-industry">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    required
                    data-testid="kaizen-start"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    required
                    data-testid="kaizen-end"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Objectives & Outcomes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="objective">Main Objective *</Label>
                <Textarea
                  id="objective"
                  value={formData.objective}
                  onChange={(e) => handleChange('objective', e.target.value)}
                  placeholder="What is the primary goal of this event?"
                  rows={2}
                  required
                  data-testid="kaizen-objective"
                />
              </div>

              <div className="space-y-2">
                <Label>Expected Outcomes</Label>
                <div className="flex gap-2">
                  <Input
                    value={outcomeInput}
                    onChange={(e) => setOutcomeInput(e.target.value)}
                    placeholder="Add an expected outcome"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOutcome())}
                    data-testid="kaizen-outcome-input"
                  />
                  <Button type="button" onClick={addOutcome} variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.expected_outcomes.length > 0 && (
                  <ul className="space-y-2 mt-3">
                    {formData.expected_outcomes.map((outcome, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm bg-muted p-2 rounded-sm">
                        <Target className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="flex-1">{outcome}</span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeOutcome(idx)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/kaizen')}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-kaizen">
              {loading ? 'Creating...' : 'Create Event'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

// Kaizen Event Detail
export const KaizenDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadEvent();
  }, [id]);

  const loadEvent = async () => {
    try {
      const data = await getKaizenEvent(id);
      setEvent(data);
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/kaizen');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      await updateKaizenEvent(id, { status });
      setEvent({ ...event, status });
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await deleteKaizenEvent(id);
      toast.success('Event deleted');
      navigate('/kaizen');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this Kaizen event? It can be restored from the Archive page.')) return;
    try {
      await archiveResource('kaizen', id);
      toast.success('Event archived');
      navigate('/kaizen');
    } catch (error) {
      toast.error('Failed to archive event');
    }
  };

  const canEdit = isAdmin || event?.created_by === user?.user_id;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!event) return null;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="kaizen-detail-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/kaizen')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="capitalize">{event.industry}</Badge>
                <Badge className={statusColors[event.status]}>
                  {event.status.replace('_', ' ')}
                </Badge>
              </div>
              <h1 className="text-2xl font-bold tracking-tight">{event.title}</h1>
              <p className="text-muted-foreground font-body mt-1">{event.description}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {canEdit && event.status !== 'completed' && (
              <>
                {event.status === 'planned' && (
                  <Button onClick={() => updateStatus('in_progress')} disabled={saving}>
                    <Play className="h-4 w-4 mr-2" /> Start Event
                  </Button>
                )}
                {event.status === 'in_progress' && (
                  <Button onClick={() => updateStatus('completed')} disabled={saving}>
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Complete
                  </Button>
                )}
              </>
            )}
            {canEdit && event.status === 'completed' && (
              <Button variant="outline" size="sm" onClick={handleArchive} data-testid="archive-kaizen-btn">
                <Archive className="h-4 w-4 mr-2" /> Archive
              </Button>
            )}
            {isAdmin && (
              <Button variant="destructive" size="icon" onClick={handleDelete}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Target Area</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-semibold">{event.area}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Duration</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-semibold">
                {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Participants</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-semibold">{event.participants?.length || 0} team members</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Event Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-5 rounded-lg border border-cyan-500/20 bg-cyan-500/5">
                <h4 className="font-semibold text-sm text-cyan-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Objective
                </h4>
                <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{event.objective || 'Not defined'}</p>
              </div>
              <div className="p-5 rounded-lg border border-emerald-500/20 bg-emerald-500/5">
                <h4 className="font-semibold text-sm text-emerald-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Expected Outcomes
                </h4>
                {event.expected_outcomes?.length > 0 ? (
                  <ul className="space-y-2">
                    {event.expected_outcomes.map((outcome, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">No outcomes defined</p>
                )}
              </div>
              <div className="p-5 rounded-lg border border-amber-500/20 bg-amber-500/5">
                <h4 className="font-semibold text-sm text-amber-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Actual Outcomes
                </h4>
                {event.actual_outcomes?.length > 0 ? (
                  <ul className="space-y-2">
                    {event.actual_outcomes.map((outcome, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {event.status === 'completed' ? 'No outcomes recorded' : 'Event not yet completed'}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default KaizenListPage;
